// File: src/components/registration/NfpRegistration/Step5NfpReview.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { nfpApi } from '../../../services/nfpApi';
import { useNfpReg } from '../../../contexts/NfpRegContext';
import { ROLE_LABELS } from './roleOptions';

const EMPTY = Object.freeze({});
const EMPTY_ARR = Object.freeze([]);

function SectionCard({ title, children, right }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <h3 style={styles.cardTitle}>{title}</h3>
        {right}
      </div>
      <div style={styles.cardBody}>{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  const display =
    value === null || value === undefined || value === '' ? (
      <span style={styles.emptyValue}>—</span>
    ) : (
      value
    );
  return (
    <div style={styles.row}>
      <div style={styles.rowLabel}>{label}</div>
      <div style={styles.rowValue}>{display}</div>
    </div>
  );
}

function recognitionLabel(type) {
  switch ((type || '').trim()) {
    case 'irs_determination_letter':
      return 'IRS determination letter';
    case 'parent_group_exemption':
      return 'Covered under a parent organization’s group exemption / group ruling';
    case 'religious_no_separate_letter':
      return 'Church or religious organization without a separate IRS determination letter';
    case 'government_unit_public_entity':
      return 'Government unit / public entity';
    case 'other_not_sure':
      return 'Other / Not sure';
    default:
      return '';
  }
}

export default function Step5NfpReview({ onBack }) {
  const { draft, registration } = useNfpReg();
  const navigate = useNavigate();

  const account    = React.useMemo(() => draft?.account ?? EMPTY, [draft?.account]);
  const entity     = React.useMemo(() => draft?.entity ?? EMPTY, [draft?.entity]);
  const proof      = React.useMemo(() => draft?.proof ?? EMPTY, [draft?.proof]);
  const locState   = React.useMemo(() => draft?.locations ?? EMPTY, [draft?.locations]);
  const agreements = React.useMemo(() => draft?.agreements ?? EMPTY, [draft?.agreements]);

  // Branding now handled in dashboard (post-registration). Keep read-only echoes if present.
  const branding = React.useMemo(() => ({
    logoUrl: entity.logoUrl || '',
    whySupportShort: entity.shortDescription || '',
    images: Array.isArray(draft?.branding?.images) ? draft.branding.images : EMPTY_ARR,
  }), [entity.logoUrl, entity.shortDescription, draft?.branding?.images]);

  const structure   = (entity.structure || '').trim(); // 'Independent','Chapter/Affiliate', etc.
  const taxClass    = (entity.taxClassification || '').trim();
  const recognition = (entity.recognitionType || '').trim();
  const publicName  = (entity.publicName || entity.legalName || '').trim();
  const missionCats = Array.isArray(entity.missionCategories) ? entity.missionCategories : EMPTY_ARR;

  const hasOwnEin = (
    entity.hasOwnEin === true ||
    entity.hasOwnEin === 'yes' ||
    entity.hasOwnEin === 'true'
  );
  const creatorIsSignatory = !(account.isSignatory === false || account.isSignatory === 'no');

  // EIN requirements mirrored from Step2 rules
  const einRequirement = React.useMemo(() => {
    switch (structure) {
      case 'Independent':
      case '509(a)(3) Supporting Org':
        return { requireLocal: true,  requireParent: false };
      case 'Umbrella/Parent':
        return { requireLocal: false, requireParent: true };
      case 'Chapter/Affiliate':
      case 'Religious / Faith-Based Organization':
      case 'Education / Health / Government Affiliated':
        return { requireLocal: hasOwnEin, requireParent: !hasOwnEin };
      default:
        return { requireLocal: false, requireParent: false };
    }
  }, [structure, hasOwnEin]);

  // Step3 data
  const serviceAreas   = Array.isArray(locState.serviceAreas) ? locState.serviceAreas : EMPTY_ARR;
  const hasPhysical    = !!(locState.hasPhysicalLocations);
  const addedLocations = Array.isArray(locState.locations) ? locState.locations : EMPTY_ARR;
  const hasMarkedHqLocation = addedLocations.some((loc) => loc?.isHq);
  const hq             = locState.hq || EMPTY;

  // Agreements — new shape: enrollmentAcceptance + (optional) signatoryAcceptance
  const enrollAcceptedAt = agreements?.enrollmentAcceptance?.acceptedAt || agreements?.acceptedAt; // back-compat
  const signatoryAcceptedAt = agreements?.signatoryAcceptance?.acceptedAt || null;

  // Gating (aligns with backend blockers)
  const gating = React.useMemo(() => {
    const missing = [];

    // Core entity requirements
    if (!entity.legalName) missing.push('Legal entity name');
    if (!structure)        missing.push('Organization Type');
    if (!taxClass)         missing.push('Tax classification');
    if (!recognition)      missing.push('Status recognition');

    if (recognition === 'parent_group_exemption') {
      if (!entity.parentOrganizationName) missing.push('Parent Organization Name');
      if (!entity.isRecognizedByParent) missing.push('Parent recognition status');
    }
    if (recognition === 'religious_no_separate_letter') {
      if (!entity.isCoveredByParentGroupExemption) missing.push('Covered by parent group exemption');
      if (entity.isCoveredByParentGroupExemption === 'yes' && !entity.parentOrganizationName) {
        missing.push('Parent Organization Name');
      }
    }
    if (recognition === 'government_unit_public_entity' && !entity.governmentEntityName) {
      missing.push('Government Entity Name');
    }
    if (recognition === 'other_not_sure' && !entity.recognitionNotes) {
      missing.push('Status recognition notes');
    }

    if (einRequirement.requireLocal && !entity.ein)        missing.push('Local EIN');
    if (einRequirement.requireParent && !entity.parentEin) missing.push('Parent/Institution EIN');

    if (hasPhysical) {
      if (!addedLocations.length) missing.push('At least one physical location');
      if (!serviceAreas.length && !hasMarkedHqLocation) {
        missing.push('Areas served or marked HQ location');
      }
    } else {
      const hasCity = !!hq?.address?.city;
      const hasState = !!hq?.address?.state;
      if (!hasCity || !hasState) {
        missing.push('HQ details (city and state)');
      }
    }

    // Terms & Conditions
    // NEW: always require enrollment acceptance by the registrant
    const hasEnrollmentAcceptance = !!enrollAcceptedAt;
    if (!hasEnrollmentAcceptance) missing.push('Master Terms not accepted by registrant');

    // If the registrant is NOT a signatory, make sure we captured invite details now
    if (!creatorIsSignatory) {
      if (!account?.inviteSignatory?.email) missing.push('Signatory invite: email');
      if (!account?.inviteSignatory?.name)  missing.push('Signatory invite: name');
      // (title optional, but add it if you want strictness)
    }

    return { missing, canSubmit: missing.length === 0 };
  }, [
    entity, structure, taxClass, recognition, einRequirement,
    serviceAreas.length, hasPhysical, addedLocations.length, hasMarkedHqLocation,
    hq?.address?.city, hq?.address?.state,
    enrollAcceptedAt, account?.inviteSignatory?.email, account?.inviteSignatory?.name,
    creatorIsSignatory
  ]);

  // FYI-only visibility checklist (images/logo are post-registration now)
  const visibilityChecklist = React.useMemo(() => {
    return [
      { label: 'Areas served added',       ok: serviceAreas.length > 0 },
      { label: 'HQ/root market provided',  ok: hasPhysical ? hasMarkedHqLocation : !!(hq?.address?.city && hq?.address?.state) },
      { label: 'Why Support text present (post-registration)', ok: !!branding.whySupportShort },
      { label: 'Logo uploaded (post-registration)',            ok: !!branding.logoUrl },
      { label: 'Master Terms accepted by registrant',          ok: !!enrollAcceptedAt },
      { label: 'Org signatory acceptance on file (can be completed later)', ok: !!signatoryAcceptedAt },
    ];
  }, [serviceAreas.length, hasPhysical, hasMarkedHqLocation, hq?.address?.city, hq?.address?.state, branding.whySupportShort, branding.logoUrl, enrollAcceptedAt, signatoryAcceptedAt]);

  const [busy, setBusy]   = React.useState(false);
  const [error, setError] = React.useState('');
  const [okMsg, setOkMsg] = React.useState('');

  const handleSubmit = async () => {
    if (!gating.canSubmit || busy) return;
    if (!registration?._id) {
      setError('No registration in progress.');
      return;
    }
    setBusy(true); setError(''); setOkMsg('');
    try {
      const completion = await nfpApi.submitFinalOnboarding(registration._id);
      const pendingSig = !creatorIsSignatory;
      const successMessage =
        completion?.message ||
        (pendingSig
          ? 'Registration submitted — pending org signatory acceptance.'
          : 'Registration submitted.');
      setOkMsg(successMessage);
      navigate('/nfp', { replace: true });
    } catch (e) {
      setError(e?.message || 'Failed to submit registration.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.stack}>
      <h2 style={styles.header}>Review &amp; Submit</h2>

      {error && <div style={styles.alertError}>{error}</div>}
      {okMsg && <div style={styles.alertSuccess}>{okMsg}</div>}

      {!gating.canSubmit && (
        <SectionCard
          title="Needs Attention Before Submit"
          right={<span style={styles.missingPill}>Missing {gating.missing.length}</span>}
        >
          <ul style={styles.bulletList}>
            {gating.missing.map((m) => (<li key={m}>{m}</li>))}
          </ul>
        </SectionCard>
      )}

      {/* Account */}
      <SectionCard title="Account">
        <Row label="Email" value={account.email} />
        <Row label="Phone" value={account.phone} />
        <Row label="Role" value={ROLE_LABELS[account.role] || account.role} />
        <Row label="Authorized Signatory" value={creatorIsSignatory ? 'Yes' : 'No'} />
        {!creatorIsSignatory && (
          <>
            <Row label="Signatory Name" value={account.inviteSignatory?.name} />
            <Row label="Signatory Email" value={account.inviteSignatory?.email} />
            <Row label="Signatory Title" value={account.inviteSignatory?.title} />
          </>
        )}
      </SectionCard>

      {/* Entity */}
      <SectionCard title="Entity">
        <Row label="Legal name" value={entity.legalName} />
        <Row label="Public-facing name" value={publicName} />
        <Row label="Organization Type" value={structure} />
        <Row label="Tax classification" value={taxClass} />
        <Row label="Status recognition" value={recognitionLabel(recognition)} />
        {einRequirement.requireLocal && <Row label="Local EIN" value={entity.ein} />}
        {einRequirement.requireParent && <Row label="Parent/Institution EIN" value={entity.parentEin} />}
        {entity.parentOrganizationName && <Row label="Parent Organization Name" value={entity.parentOrganizationName} />}
        {entity.gen && <Row label="Group Exemption (GEN)" value={entity.gen} />}
        {entity.isRecognizedByParent && <Row label="Recognized by parent" value={entity.isRecognizedByParent} />}
        {entity.isCoveredByParentGroupExemption && <Row label="Covered by parent group exemption" value={entity.isCoveredByParentGroupExemption} />}
        {entity.chapterUnitId && <Row label="Chapter/Unit ID" value={entity.chapterUnitId} />}
        {entity.governmentEntityName && <Row label="Government Entity Name" value={entity.governmentEntityName} />}
        {entity.governmentDivision && <Row label="Department / Division" value={entity.governmentDivision} />}
        {entity.recognitionNotes && <Row label="Status recognition notes" value={entity.recognitionNotes} />}
        <Row
          label="Payout intent"
          value={
            entity.payoutIntent === 'split'
              ? `Split (Local ${Number(entity.localPct ?? 0)}% / Parent ${Number(entity.parentPct ?? 0)}%)`
              : (entity.payoutIntent === 'parent' ? 'Pay Parent' : 'Pay Local Unit')
          }
        />
        {missionCats.length > 0 && (
          <Row label="Mission categories" value={missionCats.join(', ')} />
        )}
      </SectionCard>

      {/* Proof & Compliance (echo only) */}
      <SectionCard title="Proof & Compliance">
        <Row label="Status recognition" value={recognitionLabel(recognition)} />
        {/* If uploads are tracked in draft.proof, show simple signal */}
        {(proof.letterOfDetermination || proof.operatingUnderGenDoc || proof.governmentUnitDoc || proof.chapterAuthorization || proof.otherSupportingDoc) ? (
          <Row label="Uploaded docs" value="Provided now" />
        ) : (
          <Row label="Uploaded docs" value="Add later from Verification & Documents" />
        )}
        <div style={styles.sectionNote}>
          Verification documents do not block registration. You can upload and submit them for review later from
          <span style={{ fontWeight: 600 }}> Verification &amp; Documents</span> in the dashboard.
        </div>
      </SectionCard>

      {/* Areas, HQ, and Locations */}
      <SectionCard title="Areas & Locations">
        <Row label="Has physical locations?" value={hasPhysical ? 'Yes' : 'No'} />
        <Row label="Areas served" value={serviceAreas.length ? serviceAreas.join('; ') : '—'} />
        {!hasPhysical && (
          <>
            <Row label="HQ City" value={hq?.address?.city} />
            <Row label="HQ State" value={hq?.address?.state} />
            <Row label="HQ Website" value={hq?.website} />
          </>
        )}
        {hasPhysical && addedLocations.length > 0 && (
          <div style={styles.cardSection}>
            <div style={styles.cardSubheader}>Physical Locations</div>
            <ul style={styles.bulletList}>
              {addedLocations.map((l, i) => (
                <li key={i}>
                  {(l.name || 'Unlabeled')} — {(l.addressText || l.address || 'No address')}
                  {l.website ? ` — ${safeHost(l.website)}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </SectionCard>

      {/* Branding & Story (post-registration) */}
      <SectionCard title="Branding & Story (completed in Dashboard after registration)">
        <Row label="Logo" value={branding.logoUrl ? 'Uploaded' : '—'} />
        <Row label="Why Support (short)" value={branding.whySupportShort} />
        <Row label="Images" value={Array.isArray(branding.images) ? `${branding.images.length} image(s)` : '—'} />
      </SectionCard>

      {/* Agreements */}
      <SectionCard
        title="Agreements"
        right={
          <div style={{ display: 'flex', gap: 12 }}>
            <span style={enrollAcceptedAt ? styles.statusPositive : styles.statusNegative}>
              {enrollAcceptedAt ? 'Registrant Accepted' : 'Registrant Not Accepted'}
            </span>
            <span style={signatoryAcceptedAt ? styles.statusPositive : styles.statusWarn}>
              {signatoryAcceptedAt ? 'Org Signatory Accepted' : 'Org Signatory Pending'}
            </span>
          </div>
        }
      >
        <Row label="Terms version" value={agreements.termsVersion} />
        <Row label="Registrant accepted at" value={enrollAcceptedAt ? new Date(enrollAcceptedAt).toLocaleString() : '—'} />
        {agreements?.enrollmentAcceptance?.accepter && (
          <>
            <Row label="Registrant name"  value={agreements.enrollmentAcceptance.accepter?.name} />
            <Row label="Registrant email" value={agreements.enrollmentAcceptance.accepter?.email} />
            <Row label="Registrant title" value={agreements.enrollmentAcceptance.accepter?.title} />
          </>
        )}
        {signatoryAcceptedAt && agreements?.signatoryAcceptance?.signer && (
          <>
            <Row label="Org signer name"  value={agreements.signatoryAcceptance.signer?.name} />
            <Row label="Org signer email" value={agreements.signatoryAcceptance.signer?.email} />
            <Row label="Org signer title" value={agreements.signatoryAcceptance.signer?.title} />
          </>
        )}
      </SectionCard>

      {/* Visibility checklist (FYI) */}
      <SectionCard title="Consumer App Visibility Checklist (FYI)">
        <ul style={styles.checklist}>
          {visibilityChecklist.map((it) => (
            <li key={it.label} style={styles.checkItem}>
              <span
                style={{
                  ...styles.checkDot,
                  background: it.ok ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.08)',
                  color: it.ok ? '#34D399' : 'rgba(255,255,255,0.5)',
                }}
                aria-hidden="true"
              >
                {it.ok ? '✓' : '•'}
              </span>
              <span style={{ opacity: it.ok ? 0.95 : 0.7 }}>{it.label}</span>
            </li>
          ))}
        </ul>
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
          Logo and images are captured in the Dashboard after registration; they don’t block submission.
        </div>
      </SectionCard>

      {/* Actions */}
      <div style={styles.actions}>
        <button type="button" onClick={onBack} className="secondary">
          Back
        </button>
        <button
          type="button"
          disabled={!gating.canSubmit || busy}
          onClick={handleSubmit}
          className="primary"
          title={!gating.canSubmit ? 'Complete required fields before submitting' : 'Submit'}
          style={!gating.canSubmit || busy ? styles.primaryDisabled : undefined}
        >
          {busy ? 'Submitting…' : 'Complete Registration'}
        </button>
      </div>
    </div>
  );
}

/* -------------------- utils & styles -------------------- */
function safeHost(url) {
  try { return new URL(url).hostname; } catch { return url; }
}

const styles = {
  stack: { display: 'grid', gap: 24 },
  header: { fontSize: 24, fontWeight: 700, margin: 0 },
  alertError: {
    border: '1px solid rgba(248,113,113,0.4)',
    borderRadius: 12, padding: '10px 14px', color: '#FCA5A5', fontSize: 13
  },
  alertSuccess: {
    border: '1px solid rgba(16,185,129,0.4)',
    borderRadius: 12, padding: '10px 14px', color: '#6EE7B7', fontSize: 13
  },
  card: {
    borderRadius: 16, border: '1px solid rgba(255,255,255,0.12)', padding: 18, background: 'rgba(255,255,255,0.05)'
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 },
  cardTitle: { fontSize: 16, fontWeight: 600, margin: 0 },
  cardBody: { fontSize: 13, display: 'grid', gap: 8 },
  sectionNote: {
    marginTop: 10,
    fontSize: 12,
    lineHeight: 1.5,
    opacity: 0.78,
  },
  row: { display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12, padding: '4px 0' },
  rowLabel: { opacity: 0.7, fontSize: 13 },
  rowValue: { fontSize: 13 },
  emptyValue: { opacity: 0.5, fontStyle: 'italic' },
  missingPill: { color: '#F87171', fontSize: 13, fontWeight: 600 },
  bulletList: { margin: 0, paddingLeft: 18, display: 'grid', gap: 6, fontSize: 13 },
  cardSection: { marginTop: 12, display: 'grid', gap: 4 },
  cardSubheader: { fontSize: 13, fontWeight: 600, opacity: 0.9 },
  statusPositive: { color: '#34D399', fontSize: 13, fontWeight: 600 },
  statusNegative: { color: '#F87171', fontSize: 13, fontWeight: 600 },
  statusWarn: { color: '#F59E0B', fontSize: 13, fontWeight: 600 },
  checklist: { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 },
  checkItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 },
  checkDot: { display: 'inline-flex', width: 20, height: 20, alignItems: 'center', justifyContent: 'center', borderRadius: '50%', fontSize: 11, fontWeight: 700 },
  actions: { display: 'flex', justifyContent: 'space-between', paddingTop: 8, gap: 12 },
  primaryDisabled: { opacity: 0.5, cursor: 'not-allowed' },
};
